// De dónde saca el agente una foto para meterla en un moodboard —
// ADR-2026-09-13-el-agente-arma-moodboards.
//
// Todas las fuentes terminan en lo mismo: una URL FIRMADA de nuestro storage, que
// `subirFotoDesdeUrl` baja y vuelve a subir al bucket de moodboards. Se eligió ese camino
// —y no copiar el objeto entre buckets— porque las cinco fuentes viven en buckets
// distintos con paths distintos, mientras que "conseguir una URL firmada" ya está
// resuelto para cada una en su propio módulo.
//
// El vocabulario de `source` es el MISMO de `reference_images` en las generaciones: el
// modelo ya lo habla, y dos taxonomías para lo mismo se terminan contradiciendo.

import { fetchBrandContext } from '../pages/v2/agentMemoryApi';
import { BUCKET_ADJUNTOS_DEL_CHAT } from '../pages/v2/agentAttachments';
import type { MockProductV2 } from '../pages/v2/mockData';
import { fotoDeModeloParaReferencia, type ModelV2 } from '../pages/v2/modelsApi';
import { fetchFotos, MOODBOARDS_BUCKET } from '../pages/v2/moodboardsApi';
import { getProductFullImageUrl } from '../pages/v2/productsApi';
import { firmarEnLote } from '../utils/firmarEnLote';
import { preloadGeneratedImages } from '../utils/preloadGeneratedImages';
import {
  type FotoPedida,
  type Resueltas,
} from './editarMoodboard';
import { elegirMoodboard, type MoodboardResumen } from './verMoodboard';

/** Una imagen que el usuario adjuntó y que quedó guardada en su mensaje. */
export interface AdjuntaDelChat {
  storagePath: string;
  bucket?: string;
}

export interface ContextoDeFuentes {
  companyId: string | null;
  userId: string | null;
  productos: MockProductV2[];
  modelos: ModelV2[];
  /** Las imágenes del último mensaje del usuario que las tenga, en orden. */
  adjuntas: AdjuntaDelChat[];
  /** Una adjunta puntual por su id ("m1-2", "web-…"): lo resuelve `adjuntoPorId`. */
  adjuntaPorId?: (id: string) => AdjuntaDelChat | null;
  carpetas: MoodboardResumen[];
}

/** Una adjunta sin bucket es una subida al chat: vive en el bucket de los adjuntos, el mismo
 *  default de `signReferenceImage`. Antes decía `generadas` (el de las creaciones, que sí
 *  guardan su bucket), y toda subida al chat salía "no la encontré"
 *  (ADR-2026-10-20-las-adjuntas-se-buscan-en-su-bucket). */
const BUCKET_ADJUNTOS_POR_DEFECTO = BUCKET_ADJUNTOS_DEL_CHAT;

/**
 * Resuelve cada foto pedida a una URL firmada. Nunca tira: lo que no se pudo resolver
 * vuelve en `fallidas` con el motivo, para que el agente se lo pueda contar al usuario en
 * vez de que se caiga la llamada entera por un id equivocado.
 */
export async function resolverFuentesDeFoto(
  pedidas: FotoPedida[],
  ctx: ContextoDeFuentes,
): Promise<Resueltas> {
  const out: Resueltas = { ok: [], fallidas: [] };
  // Las carpetas de origen se resuelven una sola vez aunque se pidan varias fotos de la
  // misma: cada `fetchFotos` es un viaje a la base más una tanda de firmas.
  const fotosPorCarpeta = new Map<string, string[]>();

  for (const pedida of pedidas) {
    try {
      const url = await urlDe(pedida, ctx, fotosPorCarpeta);
      if (url) out.ok.push({ pedida, url });
      else out.fallidas.push({ pedida, motivo: 'no la encontré' });
    } catch (e) {
      out.fallidas.push({ pedida, motivo: String(e instanceof Error ? e.message : e) });
    }
  }
  return out;
}

async function urlDe(
  pedida: FotoPedida,
  ctx: ContextoDeFuentes,
  cache: Map<string, string[]>,
): Promise<string | null> {
  const { companyId } = ctx;
  switch (pedida.source) {
    case 'generada': {
      if (!pedida.image_id) throw new Error('falta image_id');
      if (!companyId) throw new Error('sin empresa');
      const [img] = await preloadGeneratedImages([pedida.image_id], companyId);
      return img?.signedUrl ?? null;
    }
    case 'producto': {
      if (!pedida.product_id) throw new Error('falta product_id');
      if (!companyId) throw new Error('sin empresa');
      // El FULL y no la miniatura del catálogo: una referencia de 300 px en un moodboard
      // no sirve para nada.
      const full = await getProductFullImageUrl(companyId, pedida.product_id);
      return full ?? ctx.productos.find((p) => p.id === pedida.product_id)?.imageUrl ?? null;
    }
    case 'modelo': {
      if (!pedida.model_id) throw new Error('falta model_id');
      // La foto de presentación en resolución completa (`thumbUrl` es la miniatura del
      // picker), re-firmada: la de la lista vence a la hora
      // (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
      const m = ctx.modelos.find((mm) => String(mm.id) === String(pedida.model_id));
      return m ? await fotoDeModeloParaReferencia(m) : null;
    }
    case 'logo': {
      if (!companyId) throw new Error('sin empresa');
      const marca = await fetchBrandContext(ctx.userId, companyId);
      return marca?.logoUrl ?? null;
    }
    case 'adjunto': {
      if (pedida.adjunto_id) {
        const a = ctx.adjuntaPorId?.(pedida.adjunto_id);
        if (!a) throw new Error(`no hay ninguna imagen ${pedida.adjunto_id} en el chat`);
        const firmas = await firmarEnLote([a.storagePath], a.bucket ?? BUCKET_ADJUNTOS_POR_DEFECTO);
        return firmas.get(a.storagePath) ?? null;
      }
      const lista = ctx.adjuntas;
      if (lista.length === 0) throw new Error('el usuario no adjuntó imágenes');
      const i = (pedida.numero ?? 1) - 1;
      const adjunta = lista[i];
      if (!adjunta) throw new Error(`adjuntó ${lista.length}, no hay una #${pedida.numero}`);
      const firmas = await firmarEnLote(
        [adjunta.storagePath],
        adjunta.bucket ?? BUCKET_ADJUNTOS_POR_DEFECTO,
      );
      return firmas.get(adjunta.storagePath) ?? null;
    }
    case 'moodboard': {
      if (!pedida.moodboard_origen) throw new Error('falta moodboard_origen');
      if (!companyId) throw new Error('sin empresa');
      const carpeta = elegirMoodboard(pedida.moodboard_origen, ctx.carpetas);
      if (!carpeta) throw new Error(`no hay ninguna carpeta "${pedida.moodboard_origen}"`);
      let urls = cache.get(carpeta.id);
      if (!urls) {
        const fotos = await fetchFotos({ companyId, moodboardId: carpeta.id });
        const firmas = await firmarEnLote(
          fotos.map((f) => f.path),
          MOODBOARDS_BUCKET,
        );
        urls = fotos.map((f) => firmas.get(f.path) ?? '');
        cache.set(carpeta.id, urls);
      }
      if (urls.length === 0) throw new Error(`"${carpeta.nombre}" está vacía`);
      const url = urls[(pedida.numero ?? 1) - 1];
      if (!url) throw new Error(`"${carpeta.nombre}" tiene ${urls.length} foto(s)`);
      return url;
    }
  }
}
